/**
 * @deprecated Replaced by manager/ConsumerInsightsPage.jsx (E-CI / NX-006).
 * Retained temporarily for reference. Safe to delete after QA sign-off.
 */
import React, { useState, useEffect } from 'react';
import { fetchConsumerInsights, addToPriorityQueue } from '../../utils/api';
import DataFreshness from './DataFreshness';
import ProblemsOnlyToggle from '../common/ProblemsOnlyToggle';
import { Button } from '../ui';

function ConsumerInsights({ unitId, serviceLineId, level }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showProblemsOnly, setShowProblemsOnly] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const insightsData = await fetchConsumerInsights({ unitId, serviceLineId, level });
        setData(insightsData);
      } catch (error) {
        console.error('Error loading consumer insights:', error);
      }
      setLoading(false);
    };

    loadData();
  }, [unitId, serviceLineId, level]);

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="grid grid-cols-3 gap-6">
          {[1,2,3].map(i => (
            <div key={i} className="bg-white rounded-lg shadow p-6">
              <div className="h-16 bg-gray-200 rounded"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <p className="text-gray-600">No consumer insights data available</p>
      </div>
    );
  }

  const sentiment = data.sentiment || { positive: 0, neutral: 0, negative: 0 };
  const mostPraised = data.mostPraised || [];
  const areasForImprovement = data.areasForImprovement || [];
  const problemCount = [
    (data.cahps?.score || 0) < (data.cahps?.nationalAverage || 0),
    (data.jdPower?.score || 0) < (data.jdPower?.industryAverage || 0),
    (sentiment.negative || 0) > 10
  ].filter(Boolean).length;

  const handleAddToQueue = async (payload) => {
    try {
      await addToPriorityQueue({
        title: payload.title,
        description: payload.metric || '',
        category: 'Consumer',
        priority: payload.priority || 'medium',
        estimatedTime: 45,
        dueDate: new Date().toISOString(),
        type: 'task',
        ...payload
      });
    } catch (err) {
      console.error('Add to queue failed:', err);
    }
  };

  return (
    <div className="dashboard-container">
      <div className="flex justify-end">
        <ProblemsOnlyToggle
          enabled={showProblemsOnly}
          onChange={setShowProblemsOnly}
          count={problemCount}
        />
      </div>
      {/* Top Scores - Consistent card style */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative border-l-4 border-l-aqua">
          <div className="p-5">
            <div className="text-[13px] text-text-dark-secondary font-normal mb-2 block">CAHPS Score</div>
            <div className="text-[34px] font-bold text-text-dark tracking-tight mb-1.5 block">{data.cahps?.score || 0}</div>
            <div className={`flex items-center gap-1 text-[13px] mb-4 ${(data.cahps?.trend || 0) >= 0 ? 'text-green-nn' : 'text-red-critical'}`}>
              {Math.abs(data.cahps?.trend || 0)} pts
            </div>
            <div className="text-xs text-gray-500 mb-2">Nat&apos;l Avg: {data.cahps?.nationalAverage || 0}</div>
            <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-nn-border">
              <Button
                variant="default"
                size="sm"
                type="button"
                onClick={() => handleAddToQueue({ title: `Improve CAHPS (${data.cahps?.score || 0} vs nat'l ${data.cahps?.nationalAverage || 0})`, metric: 'CAHPS Score', priority: (data.cahps?.score || 0) < (data.cahps?.nationalAverage || 0) ? 'high' : 'medium' })}
              >
                Add to Queue
              </Button>
            </div>
          </div>
        </div>

        <div className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative border-l-4 border-l-aqua">
          <div className="p-5">
            <div className="text-[13px] text-text-dark-secondary font-normal mb-2 block">JD Power Score</div>
            <div className="text-[34px] font-bold text-text-dark tracking-tight mb-1.5 block">{data.jdPower?.score || 0}</div>
            <div className={`flex items-center gap-1 text-[13px] mb-4 ${(data.jdPower?.trend || 0) >= 0 ? 'text-green-nn' : 'text-red-critical'}`}>
              {Math.abs(data.jdPower?.trend || 0)} pts
            </div>
            <div className="text-xs text-gray-500 mb-2">Industry: {data.jdPower?.industryAverage || 0}</div>
            <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-nn-border">
              <Button
                variant="default"
                size="sm"
                type="button"
                onClick={() => handleAddToQueue({ title: `Improve JD Power (${data.jdPower?.score || 0})`, metric: 'JD Power Score', priority: (data.jdPower?.score || 0) < (data.jdPower?.industryAverage || 0) ? 'high' : 'medium' })}
              >
                Add to Queue
              </Button>
            </div>
          </div>
        </div>

        <div className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative border-l-4 border-l-aqua">
          <div className="p-5">
            <div className="text-[13px] text-text-dark-secondary font-normal mb-2 block">Google Reviews</div>
            <div className="text-[34px] font-bold text-text-dark tracking-tight mb-1.5 block">{data.google?.rating || 0} ★</div>
            <div className="flex items-center gap-1 text-[13px] mb-4 text-text-dark-secondary">{(data.google?.totalReviews || 0).toLocaleString()} reviews</div>
            <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-nn-border">
              <Button
                variant="default"
                size="sm"
                type="button"
                onClick={() => handleAddToQueue({ title: 'Monitor Google review trends', metric: 'Google Reviews' })}
              >
                Add to Queue
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Google Reviews - Sentiment Analysis */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-6">Google Reviews - Sentiment Analysis</h3>

        {/* Sentiment Boxes */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
            <div className="text-5xl font-bold text-green-700 mb-2">{sentiment.positive}%</div>
            <div className="text-sm font-semibold text-gray-700 mb-1">Positive (4-5 stars)</div>
            <div className="text-xs text-gray-600">{sentiment.positiveCount || 897} reviews</div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
            <div className="text-5xl font-bold text-yellow-700 mb-2">{sentiment.neutral}%</div>
            <div className="text-sm font-semibold text-gray-700 mb-1">Neutral (3 stars)</div>
            <div className="text-xs text-gray-600">{sentiment.neutralCount || 225} reviews</div>
          </div>

          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <div className="text-5xl font-bold text-red-700 mb-2">{sentiment.negative}%</div>
            <div className="text-sm font-semibold text-gray-700 mb-1">Negative (1-2 stars)</div>
            <div className="text-xs text-gray-600">{sentiment.negativeCount || 125} reviews</div>
          </div>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-2 gap-8">
          {/* Most Praised */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <h4 className="font-bold text-gray-900">Most Praised</h4>
            </div>

            <div className="space-y-3">
              {mostPraised.map((item, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <span className="text-sm text-gray-800">{item.label}</span>
                  <span className="text-sm font-bold text-green-600">{item.count} mentions</span>
                </div>
              ))}
            </div>
          </div>

          {/* Areas for Improvement */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <h4 className="font-bold text-gray-900">Areas for Improvement</h4>
            </div>

            <div className="space-y-3">
              {areasForImprovement.map((item, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <span className="text-sm text-gray-800">{item.label}</span>
                  <span className="text-sm font-bold text-red-600">{item.count} mentions</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Appeals & Grievances - Checkmark/X buttons, consistent card style */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-[15px] font-semibold text-text-dark">Appeals & Grievances</h3>
        </div>

        <div className="grid grid-cols-2 xl:grid-cols-4 gap-5">
          {/* Open Grievances - Checkmark/X buttons instead of gradient */}
          <div className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative border-l-4 border-l-red-critical">
            <div className="p-5">
              <div className="text-[13px] text-text-dark-secondary font-normal mb-2 block">Open Grievances</div>
              <div className="text-[34px] font-bold text-text-dark tracking-tight mb-1.5 block">{data.complaints?.open ?? 7}</div>
              <div className="flex items-center gap-1 text-[13px] mb-4 text-red-critical">Require immediate attention</div>
              <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-nn-border" style={{ display: 'flex', gap: '8px' }}>
                <Button
                  variant="success"
                  size="sm"
                  type="button"
                  style={{ flex: 1 }}
                  onClick={() => handleAddToQueue({ title: 'Review all open grievances', metric: 'Open Grievances', priority: 'high' })}
                >
                  ✓ Add All
                </Button>
                <Button variant="ghost" size="sm" type="button" className="ml-auto p-1 text-text-muted hover:text-red-critical transition-colors rounded" style={{ flex: 0 }}>
                  ✕
                </Button>
              </div>
            </div>
          </div>

          {/* Appeals in Progress - White button with border */}
          <div className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative border-l-4 border-l-yellow-nn">
            <div className="p-5">
              <div className="text-[13px] text-text-dark-secondary font-normal mb-2 block">Appeals in Progress</div>
              <div className="text-[34px] font-bold text-text-dark tracking-tight mb-1.5 block">{data.complaints?.pending ?? 4}</div>
              <div className="flex items-center gap-1 text-[13px] mb-4 text-text-dark-secondary">Pending review</div>
              <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-nn-border">
                <Button
                  variant="default"
                  size="sm"
                  type="button"
                  onClick={() => handleAddToQueue({ title: 'Review appeals approaching deadline', metric: 'Appeals in Progress', priority: 'medium' })}
                >
                  Add to Queue
                </Button>
              </div>
            </div>
          </div>

          {/* Resolved - Stats only */}
          <div className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative border-l-4 border-l-green-nn">
            <div className="p-5">
              <div className="text-[13px] text-text-dark-secondary font-normal mb-2 block">Resolved</div>
              <div className="text-[34px] font-bold text-text-dark tracking-tight mb-1.5 block">{data.complaints?.resolved ?? 23}</div>
              <div className="flex items-center gap-1 text-[13px] mb-4 text-text-dark-secondary">Last 30 days</div>
            </div>
          </div>

          {/* Avg Response Time - Stats only */}
          <div className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative border-l-4 border-l-aqua">
            <div className="p-5">
              <div className="text-[13px] text-text-dark-secondary font-normal mb-2 block">Avg Response Time</div>
              <div className="text-[34px] font-bold text-text-dark tracking-tight mb-1.5 block">{data.complaints?.avgResponseTime ?? 18}</div>
              <div className="flex items-center gap-1 text-[13px] mb-4 text-text-dark-secondary">Hours</div>
            </div>
          </div>
        </div>

        {/* Grievance Details */}
        <div className="flex items-center justify-between mb-4" style={{ marginTop: '24px' }}>
          <h4 className="text-base font-semibold text-text-dark">Open Grievances Requiring Attention</h4>
        </div>

        <div className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative">
          <div className="p-5">
            <div className="text-[13px] text-text-dark-secondary">
              {[
                { id: 1, issue: 'Long wait time complaint - Patient waited 45 min', priority: 'High', days: '2 days ago' },
                { id: 2, issue: 'Billing dispute - Incorrect charge', priority: 'High', days: '3 days ago' },
                { id: 3, issue: 'Staff interaction concern - Front desk', priority: 'Medium', days: '5 days ago' },
                { id: 4, issue: 'Appointment cancellation issue', priority: 'Medium', days: '7 days ago' },
                { id: 5, issue: 'Medical records request delay', priority: 'Low', days: '10 days ago' }
              ].map(grievance => (
                <div
                  key={grievance.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px 0',
                    borderBottom: grievance.id < 5 ? '1px solid #F3F4F6' : 'none'
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div className="text-[14px] font-semibold text-text-dark" style={{ marginBottom: '4px' }}>
                      {grievance.issue}
                    </div>
                    <div className="text-body-sm text-text-muted">
                      Priority: {grievance.priority} • {grievance.days}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '6px', marginLeft: '16px' }}>
                    <Button
                      variant="ghost"
                      size="sm"
                      type="button"
                      className="p-1 text-text-muted hover:text-green transition-colors rounded"
                      onClick={() => handleAddToQueue({
                        title: grievance.issue,
                        metric: 'Grievance',
                        priority: grievance.priority.toLowerCase()
                      })}
                    >
                      ✓ Add
                    </Button>
                    <Button variant="ghost" size="sm" type="button" className="ml-auto p-1 text-text-muted hover:text-red-critical transition-colors rounded">✕</Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <DataFreshness
        lastUpdated="6 hours ago"
        source="Press Ganey, Google Reviews"
        status="current"
      />
    </div>
  );
}

export default ConsumerInsights;
